import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { ButtonWithIcon } from '@els/els-react--button';

import './Input.scss';

export default class Input extends React.PureComponent {
  constructor(props) {
    super(props);

    const { value, validateValueInput, inputProps } = props;

    this.inputRef = (inputProps && inputProps.ref) || React.createRef();

    this.state = {
      value: validateValueInput(value) ? value : '',
    };
  }

  onChangeDebounced = debounce(() => {
    const { onChange } = this.props;
    const { value } = this.state;
    onChange(value);
    // eslint-disable-next-line react/destructuring-assignment
  }, this.props.debounceDelay);

  componentDidMount() {
    const { autofocus, preSelectText } = this.props;
    if (!autofocus) {
      return;
    }
    setTimeout(() => {
      if (!this.inputRef?.current) return;

      this.inputRef?.current?.focus();
      if (preSelectText) {
        this.inputRef?.current?.select();
      }
    });
  }

  // eslint-disable-next-line consistent-return
  componentDidUpdate(prevProps, prevState) {
    const { value } = this.props;
    if (value !== prevProps.value) {
      return this.onChangeValueHandler(value);
    }

    const { value: stateValue } = this.state;
    if (stateValue !== prevState.value) {
      return this.onChangeDebounced();
    }
  }

  onChangeHandler = (e) => {
    const { value } = e.target;
    this.onChangeValueHandler(value);
  };

  onKeyDown = (e) => {
    const { onApply, inputProps } = this.props;
    inputProps?.onKeyDown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    if (e.keyCode !== 13) {
      return;
    }

    if (onApply) {
      onApply();
    }
  };

  onChangeValueHandler(value) {
    const { validateValueInput, buildQuery } = this.props;
    if (!validateValueInput(value)) {
      return;
    }
    this.setState(
      {
        value,
      },
      () => buildQuery && buildQuery.updateInputText(value)
    );
  }

  onClearClick = () => {
    const { buildQuery, onClear } = this.props;
    this.setState(
      {
        value: '',
      },
      () => buildQuery && buildQuery.updateInputText('')
    );
    this.inputRef?.current?.focus();
    if (onClear) {
      onClear();
    }
  };

  render() {
    const {
      id,
      disabled,
      className,
      inputProps,
      placeholder,
      clearButton,
      classNameInput,
      afterLabelContent,
      validationMessage,
      isValid,
      animatedPlaceholder,
      onBlur,
      onMouseDown,
      type,
      fieldName,
    } = this.props;
    const restInputProps = { ...(inputProps || {}) };
    delete restInputProps.onKeyDown;
    const { value } = this.state;

    const showClearButton = clearButton && value.length !== 0;

    return (
      <div className={classnames(className, 'new-input')}>
        <div
          className={classnames('new-input-animated-wrapper', {
            'new-input-animated-wrapper-disabled': disabled,
            'new-input-animated-wrapper-with-before': animatedPlaceholder,
          })}>
          {/* this is a temp fix once we have login and register change RX-20036 this will be removed */}
          {id !== 'id-quick-search-input' && <p>{placeholder}</p>}
          <input
            ref={this.inputRef}
            disabled={disabled}
            value={value}
            placeholder={fieldName || placeholder}
            onBlur={onBlur}
            name={fieldName || placeholder}
            onMouseDown={onMouseDown}
            onKeyDown={this.onKeyDown}
            onChange={this.onChangeHandler}
            {...restInputProps}
            type={type || 'text'}
            id={id}
            className={classnames(classNameInput, 'c-els-field__input', {
              'new-input-input-disabled': disabled,
              'new-input-input-with-button': clearButton,
              invalid: !!validationMessage && !isValid,
              'new-input-input__autosuggest': id === 'id-quick-search-input',
            })}
          />
          {showClearButton && (
            <ButtonWithIcon
              type="link"
              size="small"
              linkIconSize="xs"
              sprite="Close"
              iconOnly
              data-e2e="e2e-clear-input-button"
              onClick={this.onClearClick}
              className="new-input-clear-button e2e-clear-input">
              Clear input
            </ButtonWithIcon>
          )}
          {afterLabelContent}
        </div>
        {validationMessage && <div className="new-input-validation-message">{validationMessage}</div>}
      </div>
    );
  }
}

Input.defaultProps = {
  debounceDelay: 300,
  value: '',
  onChange: noop,
  onBlur: noop,
  onMouseDown: noop,
  validateValueInput: () => true,
  animatedPlaceholder: false,
  clearButton: true,
};

Input.propTypes = {
  buildQuery: PropTypes.object,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  clearButton: PropTypes.bool,
  disabled: PropTypes.bool,
  debounceDelay: PropTypes.number,
  classNamePlaceholder: PropTypes.string,
  classNameInput: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validateValueInput: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onApply: PropTypes.func,
  onMouseDown: PropTypes.func,
  onClear: PropTypes.func,
  animatedPlaceholder: PropTypes.bool,
  autofocus: PropTypes.bool,
  preSelectText: PropTypes.bool,
  afterLabelContent: PropTypes.element,
  fieldName: PropTypes.string,
  validationMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]), // withValidationMessage HOC
  isValid: PropTypes.bool, // withValidationMessage HOC
};
